<script>
    import { onMount } from 'svelte';
    // Componentes de UI e Core
    import { Paper, Title, Alert, Button } from '@svelteuidev/core';

    // Componentes Basicos e de UI locais/customizados
    import DropSelecao from "../../componentes_basicos/DropSelecao.svelte";
    import DatePickerSelecao from "../../componentes_basicos/DatePickerSelecao.svelte";
    import LoadingTela from "../../componentes_basicos/LoadingTela.svelte";
    import Loading from "../../componentes_basicos/Loading.svelte";
    import CaixaDeDialogo from "../../componentes_basicos/CaixaDeDialogo.svelte";

    // Classes e Stores
    import Conexoes from "../../Classes/Conexoes";
    import Constantes from "../../Classes/Constantes";
    import { DadosLogin } from "../../Classes/Stores";

    // --- Estado do Componente ---
    let id_assessoria = $DadosLogin.id_assessoria;
    let isLoading = false; // Controla LoadingTela (chamada API principal)
    let isProcessing = false; // Controla Loading inline e desabilita inputs (pós-seleção/durante API)
    let errorMessage = ""; // Mensagens de erro para Alert/CaixaDialogo
    let infoMessage = ""; // Mensagens informativas para Alert
    let selectedPlanData = null; // Objeto completo da linha selecionada no DropSelecao
    let planWorkoutsData = null; // Dados LÓGICOS para DatePicker: { 'YYYY-MM-DD': [id_treino], ... }
    let planDisplayDataMap = {}; // Dados de EXIBIÇÃO para DatePicker: { 'YYYY-MM-DD': { dia_semana_ordem: '...', Treino: '...' }, ... }
    let datePickerKey = Date.now(); // Chave para forçar recriação dos componentes filhos

    const conexao = new Conexoes();

    // --- Configuração para Dropdown de Seleção de Plano ---
    // IMPORTANTE: A query 'consulta_usuarios_planos_mes' DEVE retornar as colunas:
    // - id_plano_unico (ou similar, para key interna do DropSelecao)
    // - id_usuario
    // - mes (numérico)
    // - ano (numérico)
    // - nome_usuario (para exibição)
    // - display_mes_ano (ex: 'Março 2024', para exibição)
    let config_selecao_plano = {
        api: Constantes.api_consultar_padrao,
        query_code: "consulta_usuarios_planos_mes_ano",
        campos_pesquisaveis: ["nome_usuario", "display_mes_ano"], // Ajuste conforme sua query
        colunas_exibidas: ["nome_usuario", "display_mes_ano"],   // Ajuste conforme sua query
        coluna_saida: 'id_plano_unico', // Usado internamente pelo DropSelecao
        selecaoUnica: true, // Garante que apenas um plano seja selecionado por vez
        titulo: "Selecione o Usuário e Mês do Plano",
        parametro_adicional: id_assessoria,
    };

    // --- Funções ---

    /**
     * Chamada quando um item é selecionado no DropSelecao.
     * Recebe o objeto completo da linha selecionada.
     */
    function handlePlanSelection(event) {
        const selecionado = event.detail;
        console.log("Plano selecionado (evento 'selecionado'):", selecionado);

        // Evita reprocessamento desnecessário se o mesmo item for clicado novamente
        if (JSON.stringify(selectedPlanData) === JSON.stringify(selecionado)) {
            return;
        }

        // Inicia o processo
        isProcessing = true;
        selectedPlanData = selecionado;
        planWorkoutsData = null; // Limpa dados lógicos antigos
        planDisplayDataMap = {}; // Limpa dados de exibição antigos
        infoMessage = "";
        errorMessage = "";

        // Chama a busca dos treinos após um pequeno delay (melhora a percepção do usuário)
        setTimeout(() => {
            fetchWorkoutsForPlan(selectedPlanData);
        }, 50);
    }

    /**
     * Limpa a seleção atual e reseta os componentes relacionados.
     */
    function clearCurrentSelection() {
        selectedPlanData = null;
        planWorkoutsData = null;
        planDisplayDataMap = {};
        infoMessage = "";
        errorMessage = "";
        datePickerKey = Date.now(); // Gera nova chave para forçar recriação
        isProcessing = false; // Reseta flag
        console.log("Seleção de plano limpa.");
        // Nota: O estado visual interno do DropSelecao pode não ser resetado
        // sem modificações no próprio componente DropSelecao.
    }

    /**
     * Busca os detalhes dos treinos na API PHP para o plano selecionado.
     * @param {object} planData Objeto contendo id_usuario, mes, ano.
     */
    async function fetchWorkoutsForPlan(planData) {
        // Validação de entrada
        if (!planData || typeof planData !== 'object' || !planData.id_usuario || !planData.mes ) { 
            errorMessage = "Seleção inválida. Informações essenciais (usuário, mês, ano) ausentes.";
            console.error("Dados do plano selecionado incompletos:", planData);
            clearCurrentSelection(); // Limpa tudo se a seleção for inválida
            isProcessing = false; // Garante que parou de processar
            isLoading = false; // Garante que parou de carregar
            return;
        }

        isLoading = true; // Ativa loading principal
        errorMessage = ""; // Limpa erros anteriores
        // Não limpa infoMessage aqui, será definida pela resposta da API
        datePickerKey = Date.now(); // Nova chave para garantir reset do DatePicker
        planWorkoutsData = null;    // Limpa dados lógicos
        planDisplayDataMap = {};    // Limpa dados de exibição

        const payload = {
            id_usuario: parseInt(planData.id_usuario),
            mes: parseInt(planData.mes),
            ano: parseInt(planData.ano),
            id_assessoria: id_assessoria
        };

        try {
            console.log("Chamando API get_treinos_mes_usuario.php com payload:", payload);
            let result = await conexao.conecta('get_treinos_mes_usuario.php', payload);
            console.log("Resposta da API get_treinos_mes_usuario.php:", result);

            if (result && result.sucesso === true) {
                 if (result.dados && result.dados.length > 0) {
                    const processedLogicData = {}; // Guarda { 'YYYY-MM-DD': [id_treino] }
                    const processedDisplayData = {}; // Guarda { 'YYYY-MM-DD': { dia_semana_ordem, Treino, ... } }
                    let validItemsFound = false;
                    console.log("Dados retornados da API:", result.dados);
                    // Processa cada treino retornado pela API
                    result.dados.forEach(item => {
                        const dateStr = item.data_original;
                        const treinoId = item.id_treino;
                        const diaOrdem = item.dia_semana_ordem;

                        // Valida dados essenciais para lógica e exibição
                        if (dateStr && typeof treinoId !== 'undefined' && diaOrdem && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                            // Popula dados lógicos
                            if (!processedLogicData[dateStr]) processedLogicData[dateStr] = [];
                            if (!processedLogicData[dateStr].includes(treinoId)) {
                                processedLogicData[dateStr].push(treinoId);
                            }

                            // Popula dados de exibição (pegando info do primeiro treino válido para o dia)
                            if (!processedDisplayData[dateStr]) {
                                processedDisplayData[dateStr] = {
                                    dia_semana_ordem: item.dia_semana_ordem,
                                    Treino: item.Treino,
                                    Intervalos: item.Intervalos,
                                    observacao: item.observacao
                                    // Adicione mais campos da API aqui se precisar exibir
                                };
                            }
                            validItemsFound = true;
                        } else {
                             console.warn("Item da API inválido ou com dados incompletos, pulando:", item);
                        }
                    });

                    // Define o estado final com base nos dados processados
                    if (validItemsFound) {
                         planWorkoutsData = processedLogicData;
                         planDisplayDataMap = processedDisplayData;
                         const nomeUser = planData.nome_usuario || `Usuário ID ${planData.id_usuario}`;
                         const mesAnoDisplay = planData.display_mes_ano || `${planData.mes}/${planData.ano}`;
                         infoMessage = `Treinos carregados para ${nomeUser} (${mesAnoDisplay}).`;
                         console.log("Dados Lógicos (preloadedData):", planWorkoutsData);
                         console.log("Dados de Exibição (displayDataMap):", planDisplayDataMap);
                    } else {
                         infoMessage = `Nenhum treino com formato válido encontrado para o período selecionado.`;
                         planWorkoutsData = {}; // Objeto vazio para DatePicker
                         planDisplayDataMap = {};
                    }
                 } else {
                    // API retornou sucesso, mas sem treinos
                    const nomeUser = planData.nome_usuario || `Usuário ID ${planData.id_usuario}`;
                    const mesAnoDisplay = planData.display_mes_ano || `${planData.mes}/${planData.ano}`;
                    infoMessage = `Nenhum treino encontrado para ${nomeUser} (${mesAnoDisplay}).`;
                    planWorkoutsData = {}; // Objeto vazio para DatePicker
                    planDisplayDataMap = {};
                 }
            } else {
                // API retornou sucesso = false
                errorMessage = result.mensagem || "Erro desconhecido ao buscar treinos na API.";
                planWorkoutsData = null; // Indica que não há dados válidos
                planDisplayDataMap = {};
            }
        } catch (error) {
            // Erro na comunicação ou processamento JS
            console.error("Erro durante fetchWorkoutsForPlan:", error);
            errorMessage = "Falha na comunicação com o servidor ao buscar treinos.";
            planWorkoutsData = null;
            planDisplayDataMap = {};
        } finally {
            // Finaliza os estados de loading
            isLoading = false;
            isProcessing = false;
        }
    }

</script>

<!-- Componentes de Feedback Global -->
<CaixaDeDialogo message={errorMessage} on:close={() => (errorMessage = "")} />
<LoadingTela {isLoading} />

<!-- Conteúdo Principal -->
{#if id_assessoria && id_assessoria > 0}
    <Paper shadow="sm" padding="md" withBorder>
        <Title order={3} mb="md">Carregar Treinos por Usuário/Mês</Title>

        <!-- Seção de Seleção (Dropdown e Botão Limpar) -->
         <div class="d-flex align-items-end gap-2 mb-3"> 
            <div class="flex-grow-1">
                 <DropSelecao
                    config={config_selecao_plano}
                    on:selecionado={handlePlanSelection}
                    disabled={isLoading || isProcessing}
                    let:saida={saidaDrop} 
                    initialData={selectedPlanData}
                    key={datePickerKey} 
                 />
            </div>
             {#if selectedPlanData}
                <Button
                    variant="outline"
                    color="red"
                    size="xs"
                    on:click={clearCurrentSelection}
                    disabled={isLoading || isProcessing}
                    title="Limpar seleção atual"
                >
                    <i class="fas fa-times me-1" aria-hidden="true"></i> Limpar
                </Button>
             {/if}
         </div>

         <!-- Exibição da Seleção Atual -->
         {#if selectedPlanData && !isProcessing && !isLoading}
            <p class="small text-muted mb-2">
                Plano selecionado: <strong>{selectedPlanData.nome_usuario || 'N/A'}</strong>
                ({selectedPlanData.display_mes_ano || `${selectedPlanData.mes}/${selectedPlanData.ano}`})
            </p>
         {/if}

        <!-- Feedback Inline (Loading e Mensagens) -->
        {#if isProcessing}
            <div class="text-center my-2">
                <Loading size="sm" />
                <p class="text-muted small">Carregando dados do plano...</p>
            </div>
        {/if}

        {#if infoMessage && !errorMessage && !isProcessing}
             <Alert
                 title="Informação" color="blue" withCloseButton
                 on:close={() => infoMessage = ''} class="my-2"
             >
                 <i class="fas fa-check-circle me-2" aria-hidden="true"></i>
                 {infoMessage}
             </Alert>
        {/if}
         {#if errorMessage && !isProcessing}
             <Alert
                 title="Erro" color="red" withCloseButton
                 on:close={() => errorMessage = ''} class="my-2"
             >
                 <i class="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                 {errorMessage}
             </Alert>
         {/if}


        <!-- DatePicker para Exibir/Editar Treinos -->
        {#if selectedPlanData && !isLoading && !isProcessing}
            {#if planWorkoutsData !== null} <!-- Renderiza apenas se planWorkoutsData não for null -->
                {#key datePickerKey} <!-- Garante recriação com novos dados -->
                    <DatePickerSelecao
                        preloadedData={planWorkoutsData}
                        displayDataMap={planDisplayDataMap} 
                        {id_assessoria}
                        bind:saida={planWorkoutsData} 
                    />
                {/key}
            {:else if !errorMessage}
                <!-- Mensagem se planWorkoutsData for null mas não houve erro explícito -->
                 <p class="text-center text-muted mt-3">Não foi possível carregar os detalhes dos treinos.</p>
            {/if}
        {:else if !selectedPlanData && !isLoading && !isProcessing}
             <!-- Mensagem inicial quando nada está selecionado -->
            <p class="text-center text-muted mt-3">Selecione um usuário e mês acima para visualizar os treinos.</p>
        {/if}

    </Paper>
{:else}
    <!-- Mensagem para acesso não autorizado -->
    <Alert title="Acesso Negado" color="red">
        <i class="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
        Acesso não autorizado ou ID da assessoria não definido.
    </Alert>
{/if}

<style>
   .gap-2 { gap: 0.5rem; }
   /* Melhora alinhamento de ícones em Alerts */
   .alert i {
       vertical-align: middle;
   }
   /* Ajusta botão limpar para alinhar melhor com input */
    .d-flex.align-items-end .btn {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
   }
</style>